<template>
  <div class="character-detail pb-[80px]">
    <DetailDescription :character="calData" />
    <ImageDisplay :character="calData" />
    <ImageComposition :character="calData" :character-xihe="xiheData?.data" />
    <!-- <RelativeWork
      v-if="(relativeData?.data?.results || []).length > 0"
      class="pc-only"
      :templates="relativeData?.data?.results"
    />
    <CommonScenarios
      v-if="calData?.same_application_data?.length"
      :digitalHumanList="calData?.same_application_data"
    /> -->
    <CommonRace
      v-if="calData?.same_race_data?.length"
      :digitalHumanList="calData?.same_race_data"
    />
    <ShareM />
  </div>
</template>
<script lang="ts" setup>
import { pick } from 'lodash-es'
import DetailDescription from './components/detail-description/index.vue'
import ImageDisplay from './components/image-display/index.vue'
import ImageComposition from './components/image-composition/index.vue'
import RelativeWork from './components/relative-work/index.vue'
import CommonRace from './components/common-race/index.vue'
import CommonScenarios from './components/common-scenarios/index.vue'
import ShareM from './components/share-m/index.vue'
import { getCharacter, getCharacterFromXihe } from '~/api/module/character'
import { getTemplateList } from '~/api/module/template'
import { createWorkByCharacter } from '~/features/createWork/utils'
import { ACTOR_NAV, CONFIG_NAV, CONTENT_TABS, USER_LEVEL } from '~/constants'
import { SESSION_KEY } from '~/constants/config'
import { PageKey } from '~/constants/page'
import { useStartPatch } from '~/hooks/useStarPatch'
import { useWatchPageSeoData } from '~/hooks/useSeo'
import type { Character } from '~/api/interface/character'

const route = useRoute()
const userInfo = useUserInfo()
const { $t } = useNuxtApp()

provideResourceLocation({
  source: SOURCE.CHARACTER_DETAIL,
})

function autoSuffix(t: string) {
  const suffix = isChinese() ? `-${$t('you-yan')}AI` : `-${$t('you-yan')}`
  if (t.endsWith(suffix)) {
    return t
  }

  return t + suffix
}

const { data: character, status } = useAsyncData(
  `${route.params.look_name}_backend`,
  async () => {
    const { data } = await getCharacter({
      look_name: route.params.look_name as string,
      query_star: import.meta.client,
    })

    if (data) {
      data.query_star = import.meta.client
      const tagTreeArr = Object.values(data.tag_tree)

      const industryTag = tagTreeArr.find(
        (tag) => tag.outer_id === 'character_industry'
      )

      const applicationTag = tagTreeArr.find(
        (tag) => tag.outer_id === 'character_application'
      )

      const characterIndustries: string[] = []
      const characterScenarios: string[] = []

      if (industryTag) {
        const idArr1 = data.tag_ids[industryTag.tag_id]
        if (idArr1?.length) {
          idArr1.forEach((id) => {
            const targetIndustry = Object.values(
              industryTag.children || []
            ).find((target) => target.tag_id === id)
            if (targetIndustry) {
              characterIndustries.push(targetIndustry.tag_name)
            }
          })
        }
      }

      if (applicationTag) {
        const idArr2 = data.tag_ids[applicationTag.tag_id]
        if (idArr2?.length) {
          idArr2.forEach((id) => {
            const targetScenario = Object.values(
              applicationTag?.children || []
            ).find((target) => target.tag_id === id)
            if (targetScenario) {
              characterScenarios.push(targetScenario.tag_name)
            }
          })
        }
      }

      data.seo_description =
        data.seo_description ||
        (getEnv('area') === 'CN'
          ? $t('character-detail-default-description', [
              characterIndustries[0] || '',
              data.name || '',
              characterIndustries.join('、') || '',
              characterScenarios.join('、') || '',
            ])
          : $t('tdk-character-detail-default-description', [
              data.name || '',
              characterIndustries.join('、') || '',
              characterScenarios.join('、') || '',
            ]))

      if (!data.description) {
        data.description =
          getEnv('area') === 'CN'
            ? $t('character-detail-default-description', [
                characterIndustries[0] || '',
                data.name || '',
                characterIndustries.join('、') || '',
                characterScenarios.join('、') || '',
              ])
            : $t('character-detail-default-description', [
                data.name || '',
                characterIndustries.join('、') || '',
                characterScenarios.join('、') || '',
              ])
      }

      data.seo_title = data.seo_title
        ? autoSuffix(data.seo_title)
        : $t('character-detail-title', [
            characterIndustries[0] || '',
            data.name || '',
          ]).trim()
    }

    deepOmit(data as any, ['look'])
    return data as Character
  }
)

const { calData } = useStartPatch(character, 2)

useWatchPageSeoData(character)

watch(
  [() => character.value, () => status.value],
  ([iData, iStatus]) => {
    if (!iData?.id && ['success', 'error'].includes(iStatus)) {
      throw new Error(
        `no character found for look_name: ${route.params.look_name}`
      )
    }

    if (iData?.look_name && import.meta.client) {
      /** 获取来源，获取后删除记录 */
      const character_source = sessionGetItem(SESSION_KEY.CHARACTER_SOURCE)
      const source = character_source || SOURCE.CHARACTER_DETAIL
      sessionRemoveItem(SESSION_KEY.CHARACTER_SOURCE)

      trackEvent(TRACK_EVENT.PAGE_CHARACTER_DETAIL, {
        look_name: iData.look_name,
        name: iData.name,
        tags: iData.tags,
        source,
      })
    }
  },
  { immediate: true, flush: 'sync' }
)

const { data: xiheData } = useAsyncData(`${route.params.look_name}_xihe`, () =>
  getCharacterFromXihe({
    look_name: route.params.look_name as string,
    terminal_application:
      getEnv('area') === 'CN' ? 'youyan' : 'youyan_international',
  }).then((res) => {
    deepOmit(res, [
      'asset_info_path',
      'material_ue_svn_path_hierachy',
      'mod_grp_hierachy',
      'skin_ue_svn_path_hierachy',
      'ue_asset_dir',
      'ue_mask_dir',
      'ue_skeleton_file_path',
      'ue_textures_dir',
      'render_image_oss',
    ])
    return res
  })
)

// const { data: relativeData } = useAsyncData(
//   `${route.params.look_name}_templateList`,
//   () =>
//     getTemplateList({
//       look_name: route.params.look_name as string,
//       page: 1,
//       page_size: 20,
//     })
// )

const createOps = (type: 'image' | 'person') => {
  // 前端判断当前资产用户是否可用
  if (
    userInfo.value?.rights_type &&
    character.value?.config_type &&
    character.value?.config_type > userInfo.value?.rights_type
  ) {
    const text = USER_LEVEL()[character.value?.config_type]?.name

    message.warning(`当前角色是${text}资产，如需使用请升级`)
    return
  }

  if (type === 'image') {
    createWorkByCharacter(
      character.value!.id,
      CONFIG_NAV.ACTOR,
      ACTOR_NAV.CHARACTER
    )
  } else {
    createWorkByCharacter(
      character.value!.id,
      CONFIG_NAV.CONTENT,
      CONTENT_TABS.CHARACTER
    )
  }
}

function createWork(source: SOURCE) {
  trackEvent(TRACK_EVENT.CLICK_CHARACTER_USE, {
    source,
    tags: character.value?.tags,
    id: character.value?.id,
    look_name: route.params.look_name,
    name: character.value?.name,
  })
  createOps('person')
}

function changeImage(source: SOURCE) {
  trackEvent(TRACK_EVENT.CLICK_CHARACTER_EDIT, {
    source,
    tags: character.value?.tags,
    id: character.value?.id,
    look_name: route.params.look_name,
    name: character.value?.name,
  })
  createOps('image')
}

provide('createWork', createWork)

provide('changeImage', changeImage)

useWatchPageSeoData(calData)
</script>
<style lang="scss" scoped></style>
